import React, { useState, useEffect } from 'react';
import { auth } from '../firebase';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { useLocation } from 'react-router-dom';
import { rememberAuthReturnPath } from '../routing/authReturnPath';

export default function Login() {
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [getPreApprovals, setGetPreApprovals] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [authMethod, setAuthMethod] = useState('email'); // 'email' or 'sso'

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    rememberAuthReturnPath(location.state?.from);
  }, [location.state]);

  // Autodesk SSO handler - redirects to backend Autodesk OAuth endpoint
  const handleAutodeskSSO = () => {
    // Full-page redirect required for OAuth
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';
    window.location.href = `${API_BASE_URL}/oauth/autodesk/login`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: 'radial-gradient(circle at 20% 50%, rgba(15, 23, 42, 0.9) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(30, 41, 59, 0.9) 0%, transparent 50%), linear-gradient(135deg, #0A0E1A 0%, #0F172A 25%, #1E293B 50%, #0F172A 75%, #0A0E1A 100%)',
      position: 'relative',
      display: 'flex', 
      alignItems: 'center',
      justifyContent: 'center',
      padding: isMobile ? '2rem 1rem' : '2.5rem',
    }}>
      <div style={{
        width: '100%',
        maxWidth: '1200px',
        display: isMobile ? 'flex' : 'grid',
        flexDirection: isMobile ? 'column' : undefined,
        gridTemplateColumns: isMobile ? undefined : '1fr 1fr',
        alignItems: 'stretch',
        gap: '2rem',
        position: 'relative'
      }}>
      {/* Left Panel - Platform Context (Glass Card) */}
      <div style={{ 
        width: '100%',
        height: '100%',
        minHeight: isMobile ? 'auto' : '680px',
        background: 'rgba(10, 20, 30, 0.55)',
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        borderRadius: '24px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        padding: '2.5rem',
        display: 'flex', 
        flexDirection: 'column',
        gap: '2rem',
        position: 'relative',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
      }}
      >
        {/* Inner highlight effect */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '1px',
          background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
          borderRadius: '24px 24px 0 0'
        }}></div>
        {/* Logo */}
        <h1 style={{ 
          fontSize: '1.75rem', 
          fontWeight: 'bold', 
          color: 'white',
          margin: 0
        }}>
          ConstructPay
        </h1>

        {/* Subtitle */}
        <p style={{ 
          fontSize: '1rem', 
          color: '#94A3B8',
          margin: 0,
          lineHeight: '1.6'
        }}>
          Unified financial platform for the construction ecosystem
        </p>

        {/* Headline */}
        <h2 style={{ 
          fontSize: '1.875rem', 
          fontWeight: '700', 
          color: 'white',
          margin: 0,
          lineHeight: '1.2'
        }}>
          One secure entry. Many partner experiences.
        </h2>

        {/* Subtext */}
        <p style={{ 
          fontSize: '0.9375rem', 
          color: '#94A3B8',
          margin: '0.75rem 0 0 0',
          lineHeight: '1.6'
        }}>
          Access payments, risk insights, capital tools, and partner workflows across general contractors, trade partners, lenders, and insurers.
        </p>

        {/* Chips */}
        <div style={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: '0.75rem',
          marginTop: '0.5rem'
        }}>
          {['Payments & Funding', 'Risk & Prequalification', 'Capital Stack', 'Insights & Reporting'].map((chip, idx) => (
            <span key={idx} style={{
              background: 'rgba(20, 184, 166, 0.08)',
              border: '1px solid rgba(20, 184, 166, 0.2)',
              borderRadius: '9999px',
              padding: '0.5rem 1rem',
              fontSize: '0.875rem',
              color: '#5EEAD4',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#14B8A6' }}></div>
              {chip}
            </span>
          ))}
        </div>

        {/* Footer row: Security + Links */}
        <div style={{ 
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: 'auto',
          paddingTop: '1rem',
          flexWrap: 'wrap',
          gap: '0.5rem'
        }}>
          <p style={{ 
            fontSize: '0.875rem', 
            color: '#64748B',
            margin: 0
          }}>
            Enterprise-grade security • Role-based access
          </p>
          <div style={{ 
            display: 'flex', 
            gap: '0.5rem',
            alignItems: 'center'
          }}>
            <a href="#" style={{ color: '#64748B', fontSize: '0.875rem', textDecoration: 'none' }}>Privacy</a>
            <span style={{ color: '#64748B' }}>•</span>
            <a href="#" style={{ color: '#64748B', fontSize: '0.875rem', textDecoration: 'none' }}>Terms</a>
          </div>
        </div>

        {/* Who uses ConstructPay */}
        <div style={{ marginTop: '1rem' }}>
          <h3 style={{ 
            fontSize: '0.875rem', 
            fontWeight: '600', 
            color: '#CBD5E1',
            marginBottom: '1rem'
          }}>
            Who uses ConstructPay
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
            {[
              'General Contractors',
              'Trade Partners & Specialty Contractors',
              'Financial Institutions & Lenders',
              'Insurance & Surety Partners'
            ].map((item, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ width: '4px', height: '4px', borderRadius: '50%', backgroundColor: '#8B5CF6', marginRight: '0.75rem' }}></div>
                <span style={{ color: '#94A3B8', fontSize: '0.875rem' }}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel - Authentication (Glass Card) */}
      <div style={{ 
        width: '100%',
        height: '100%',
        minHeight: isMobile ? 'auto' : '680px',
        background: 'rgba(10, 20, 30, 0.55)',
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        borderRadius: '24px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        padding: '2.5rem',
        display: 'flex', 
        flexDirection: 'column',
        position: 'relative',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
      }}
      >
        {/* Inner highlight effect */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '1px',
          background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
          borderRadius: '24px 24px 0 0'
        }}></div>
        {/* Title and Subtitle */}
        <h2 style={{ 
          fontSize: '1.5rem', 
          fontWeight: '600', 
          color: 'white',
          margin: '0 0 0.5rem 0'
        }}>
          Sign in
        </h2>
        <p style={{ 
          fontSize: '0.875rem', 
          color: '#94A3B8',
          margin: '0 0 1.5rem 0'
        }}>
          Use email credentials or a connected SSO provider.
        </p>

        {/* Segmented Toggle */}
        <div style={{ 
          display: 'flex',
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '9999px',
          padding: '4px',
          marginBottom: '1.5rem',
          gap: '4px',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <button
            type="button"
            onClick={() => setAuthMethod('email')}
            style={{
              flex: 1,
              padding: '0.625rem 1rem',
              borderRadius: '9999px',
              border: 'none',
              background: authMethod === 'email' ? 'rgba(20, 184, 166, 0.15)' : 'transparent',
              color: authMethod === 'email' ? 'white' : '#94A3B8',
              fontSize: '0.875rem',
              fontWeight: authMethod === 'email' ? '500' : '400',
              cursor: 'pointer',
              transition: 'all 0.2s',
              boxShadow: authMethod === 'email' ? '0 0 0 1px rgba(20, 184, 166, 0.3), 0 0 12px rgba(20, 184, 166, 0.15)' : 'none'
            }}
          >
            Email
          </button>
          <button
            type="button"
            onClick={() => setAuthMethod('sso')}
            style={{
              flex: 1,
              padding: '0.625rem 1rem',
              borderRadius: '9999px',
              border: 'none',
              background: authMethod === 'sso' ? 'rgba(20, 184, 166, 0.15)' : 'transparent',
              color: authMethod === 'sso' ? 'white' : '#94A3B8',
              fontSize: '0.875rem',
              fontWeight: authMethod === 'sso' ? '500' : '400',
              cursor: 'pointer',
              transition: 'all 0.2s',
              boxShadow: authMethod === 'sso' ? '0 0 0 1px rgba(20, 184, 166, 0.3), 0 0 12px rgba(20, 184, 166, 0.15)' : 'none'
            }}
          >
            SSO
          </button>
        </div>

        {/* Email Form (shown when Email is selected) */}
        {authMethod === 'email' && (
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {/* Email Field */}
            <div>
              <label style={{ 
                display: 'block', 
                fontSize: '0.875rem', 
                fontWeight: '500', 
                marginBottom: '0.5rem',
                color: 'white'
              }}>
                Email
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={{ 
                  width: '100%', 
                  padding: '0.75rem', 
                  background: 'rgba(0, 0, 0, 0.2)', 
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  borderStyle: 'inset',
                  borderRadius: '8px', 
                  fontSize: '1rem',
                  color: 'white'
                }}
                placeholder="name@company.com"
              />
            </div>

            {/* Password Field */}
            <div>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '0.5rem'
              }}>
                <label style={{ 
                  fontSize: '0.875rem', 
                  fontWeight: '500', 
                  color: 'white',
                  margin: 0
                }}>
                  Password
                </label>
                <a href="#" style={{
                  color: '#64748B',
                  fontSize: '0.75rem',
                  textDecoration: 'none',
                  fontWeight: '500'
                }}>FORGOT?</a>
              </div>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{ 
                  width: '100%', 
                  padding: '0.75rem', 
                  background: 'rgba(0, 0, 0, 0.2)', 
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  borderStyle: 'inset',
                  borderRadius: '8px', 
                  fontSize: '1rem',
                  color: 'white'
                }}
                placeholder="Enter your password"
                minLength={6}
              />
            </div>

            {/* Checkboxes */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  id="rememberMe"
                  style={{
                    width: '18px',
                    height: '18px',
                    marginRight: '0.5rem',
                    accentColor: '#8B5CF6'
                  }}
                />
                <label htmlFor="rememberMe" style={{ color: '#CBD5E1', fontSize: '0.875rem', cursor: 'pointer' }}>
                  Remember me
                </label>
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  id="preApprovals"
                  checked={getPreApprovals}
                  onChange={(e) => setGetPreApprovals(e.target.checked)}
                  style={{
                    width: '18px',
                    height: '18px',
                    marginRight: '0.5rem',
                    accentColor: '#8B5CF6'
                  }}
                />
                <label htmlFor="preApprovals" style={{ color: '#CBD5E1', fontSize: '0.875rem', cursor: 'pointer' }}>
                  Get pre-approvals
                </label>
              </div>
            </div>

            {error && (
              <div style={{ 
                background: 'rgba(220, 38, 38, 0.1)', 
                border: '1px solid rgba(220, 38, 38, 0.3)',
                color: '#FCA5A5', 
                padding: '0.75rem', 
                borderRadius: '8px', 
                fontSize: '0.875rem'
              }}>
                {error}
              </div>
            )}

            {/* Log in Button */}
            <button
              type="submit"
              disabled={loading}
              style={{ 
                width: '100%', 
                background: 'linear-gradient(135deg, #14B8A6 0%, #3B82F6 100%)',
                color: 'white', 
                padding: '0.875rem', 
                borderRadius: '8px', 
                border: 'none', 
                fontSize: '1rem', 
                fontWeight: '500', 
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1,
                boxShadow: '0 4px 12px rgba(20, 184, 166, 0.3)'
              }}
            >
              {loading ? 'Loading...' : 'Log in'}
            </button>
          </form>
        )}

        {/* SSO View (shown when SSO is selected) */}
        {authMethod === 'sso' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <p style={{ color: '#94A3B8', fontSize: '0.875rem', margin: 0 }}>
              Sign in with your organization's SSO provider.
            </p>
            <button
              type="button"
              onClick={handleAutodeskSSO}
              style={{ 
                width: '100%', 
                background: 'rgba(255, 255, 255, 0.1)', 
                color: 'white', 
                padding: '0.875rem', 
                borderRadius: '8px', 
                border: '1px solid rgba(255, 255, 255, 0.2)', 
                fontSize: '1rem', 
                fontWeight: '500', 
                cursor: 'pointer'
              }}
            >
              Continue with Autodesk
            </button>
            <p style={{ 
              color: '#64748B', 
              fontSize: '0.875rem',
              textAlign: 'center',
              margin: 0,
              lineHeight: '1.5'
            }}>
              Your access and dashboard are determined automatically after sign-in.
            </p>
          </div>
        )}

        {/* Divider and Autodesk Button (shown for Email view) */}
        {authMethod === 'email' && (
          <>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              margin: '1.5rem 0 1rem 0',
              gap: '1rem'
            }}>
              <div style={{ flex: 1, height: '1px', background: 'rgba(255, 255, 255, 0.08)' }}></div>
              <span style={{ color: '#64748B', fontSize: '0.75rem', fontWeight: '500' }}>OR</span>
              <div style={{ flex: 1, height: '1px', background: 'rgba(255, 255, 255, 0.08)' }}></div>
            </div>

            <button
              type="button"
              onClick={handleAutodeskSSO}
              style={{ 
                width: '100%', 
                background: 'rgba(255, 255, 255, 0.08)', 
                color: 'white', 
                padding: '0.875rem', 
                borderRadius: '8px', 
                border: '1px solid rgba(255, 255, 255, 0.15)', 
                fontSize: '1rem', 
                fontWeight: '500', 
                cursor: 'pointer',
                marginBottom: '1rem'
              }}
            >
              Continue with Autodesk
            </button>
          </>
        )}

        {/* Your access text - shown for email form only */}
        {authMethod === 'email' && (
          <p style={{ 
            color: '#64748B', 
            fontSize: '0.875rem',
            textAlign: 'center',
            margin: '0',
            lineHeight: '1.5'
          }}>
            Your access and dashboard are determined automatically after sign-in.
          </p>
        )}

      </div>
      </div>
    </div>
  );
}
